import { tagged } from "@mirage/service-logging";
import * as scoring from "@mirage/service-typeahead-search/service/scoring";
import { TYPEAHEAD_SEARCH_SOURCES } from "@mirage/service-typeahead-search/service/sources";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { Observable } from "rxjs";
import * as rx from "rxjs";
import * as op from "rxjs/operators";

import { isScoredUserQueryResult } from "@mirage/shared/search/user-query";
import type { ExtractTypePredicate } from "@mirage/shared/util/ts-util-types";
import { predicateFactory } from "../utils/predicate-factory";
import {
  clampSomeResultsMaxPosition,
  filterAndMergeByType,
  getDedupeKey,
  getDynamicSectionLimit,
  isRecentChat,
  isRecentQuery,
  isSuggestedQuery,
  isURLResult,
  isURLShortcut,
  orderedSectionGrouping,
  sortScoredResults,
} from "./utils";

let activePipelineUuid: string | null = null;

export type PipelineFunctionWrapper<T, K> = (
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
  auxiliarySources: typeahead.PredicateSubject<typeahead.SourceId>[],
  cacheAnalysis: typeahead.CacheAnalysis,
) => PipelineFunction<T, K>;

export type PipelineFunction<T, K> = (source: Observable<T>) => Observable<K>;

export const PIPELINE_DEBUG = false;
PIPELINE_DEBUG satisfies false;

const STREAM_TIMEOUT_MS = 3_000;
const LIVE_SOURCE_IDS = [
  typeahead.SourceId.Skeleton,
  typeahead.SourceId.Extensions,
];

const logger = tagged("service-typeahead-search/search/pipeline");

const PRIORITY_RESULT_TYPES = [
  typeahead.ResultType.SuggestedQuery,
  typeahead.ResultType.AskUserQuery,
  typeahead.ResultType.WriteUserQuery,
  typeahead.ResultType.OrganizeUserQuery,
  typeahead.ResultType.MyProfileSuggestion,
];

export function buildPipelineFunctions(
  query: string,
  mode: typeahead.Mode,
  config: typeahead.Config,
): Array<
  PipelineFunctionWrapper<typeahead.ScoredResult[], typeahead.ScoredResult[]>
> {
  const result: Array<
    PipelineFunctionWrapper<typeahead.ScoredResult[], typeahead.ScoredResult[]>
  > = [];

  result.push(dedupeResultsByUUID);

  if (
    (mode === typeahead.Mode.Default || mode === typeahead.Mode.Ask) &&
    config.isOmniboxEnabled
  ) {
    result.push(dedupeRecentChatsAndPreviousQueries);
  }

  result.push(dedupeResultsByURLAndScore);

  result.push(sortFlatResults);

  if (config.isOmniboxEnabled) {
    result.push(limitResultsInSections);
  }

  if (config.isOmniboxEnabled) {
    result.push(scoreSections);

    if (mode === typeahead.Mode.Default || !query.length) {
      result.push(sortSectionedResults);
    }
  }

  if (!config.isOmniboxEnabled) {
    result.push(applyUniqueRules_deprecated);
  } else {
    result.push(applyUniqueRules);
  }

  result.push(createTimeoutSafetyNet);

  return result;
}

/**
 * Gets sources based on sections array and returns an observable of results
 */
export function getSources(
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
  auxiliarySources: typeahead.PredicateSubject<typeahead.SourceId>[],
  cacheAnalysis: typeahead.CacheAnalysis,
): Observable<typeahead.ScoredResult[]> {
  const LOCAL_SOURCE_P_95_RESOLUTION_TIME_MS = 30;
  const currentPipelineUuid = options.pipelineUuid;

  activePipelineUuid = currentPipelineUuid;

  const callableSources = predicateFactory<typeahead.SourceId>(
    query,
    mode,
    [...TYPEAHEAD_SEARCH_SOURCES, ...auxiliarySources],
    {
      config: options.config,
      cacheAnalysis,
      sections: options.sections,
    },
  );

  const localSources: Set<typeahead.SourceId> = new Set();
  for (const source of callableSources) {
    if (LIVE_SOURCE_IDS.includes(source.id)) continue;
    localSources.add(source.id);
  }

  return rx.from(callableSources).pipe(
    op.mergeMap((source) =>
      runner(source, query, mode, options).pipe(
        op.tap(() => {
          localSources.delete(source.id);
        }),
      ),
    ),
    op.scan(
      (accumulator: typeahead.ScoredResult[], sourceResults) => [
        ...accumulator,
        ...sourceResults,
      ],
      [],
    ),
    op.debounceTime(LOCAL_SOURCE_P_95_RESOLUTION_TIME_MS),
    op.filter(() => {
      const isStillActive = activePipelineUuid === currentPipelineUuid;
      const allLocalSourcesComplete = !localSources.size;
      return allLocalSourcesComplete && isStillActive;
    }),
  );
}

export function runner(
  source: typeahead.PredicateSubject<typeahead.SourceId>,
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
): Observable<typeahead.ScoredResult[]> {
  // We can reduce this to the maximum number emitted from any section/source
  // This number is 8 currently, but we can use 15 to be safe, this is negligable
  const WARN_TOO_MANY_RESULTS_THRESHOLD = 15;
  const perSourceUUIDCountSet = new Set<string>();
  let warned = false;

  return (
    source
      .search(query, mode, options)
      .pipe(op.subscribeOn(rx.asyncScheduler))
      // swallow source-level failures
      .pipe(
        op.catchError((e) => {
          logger.error(`search source error for ${source.id}`, e);
          return rx.of([]);
        }),
      )
      .pipe(
        op.tap((results) => {
          results.forEach((result) => {
            perSourceUUIDCountSet.add(result?.uuid);
          });

          if (
            !warned &&
            perSourceUUIDCountSet.size > WARN_TOO_MANY_RESULTS_THRESHOLD
          ) {
            warned = true;
            logger.warn(
              `More than ${WARN_TOO_MANY_RESULTS_THRESHOLD} items emitted from the source ${source.id}!`,
            );
          }
        }),
      )
    // uncomment this line to add some delay for easier development
    // .pipe(rx.delay(500))
  );
}

export const aggregateResults: PipelineFunctionWrapper<
  typeahead.ScoredResult,
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.scan((acc, value) => [...acc, value], [] as typeahead.ScoredResult[]),
    );
  };
};

export const dedupeResultsByUUID: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.map((results) => {
        const seen = new Set<string>();
        return results.filter((result) => {
          const key = getDedupeKey(result);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }),
    );
  };
};

export const dedupeResultsByURLAndScore: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.map((results: typeahead.ScoredResult[]): typeahead.ScoredResult[] => {
        const seenResults: { [key: string]: typeahead.ScoredResult } = {};

        results.forEach((result) => {
          if (isURLResult(result) && result.result.url) {
            try {
              const parsedUrl = new URL(result.result.url);
              const cleanUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${parsedUrl.pathname}`;
              if (
                !seenResults[cleanUrl] ||
                seenResults[cleanUrl].score < result.score
              ) {
                seenResults[cleanUrl] = result;
              }
            } catch (e) {
              logger.warn("Invalid url during dedupe");
            }
          } else {
            const dedupeKey = getDedupeKey(result);
            if (
              !seenResults[dedupeKey] ||
              seenResults[dedupeKey].score < result.score
            ) {
              seenResults[dedupeKey] = result;
            }
          }
        });

        return Object.values(seenResults);
      }),
    );
  };
};

export const dedupeRecentChatsAndPreviousQueries: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.map((results) => {
        const recentChatTitles = new Set<string>();

        return results.filter((result) => {
          if (isRecentChat(result)) {
            const title = result.result.title;
            if (recentChatTitles.has(title)) {
              return false;
            }
            recentChatTitles.add(title);
            return true;
          }

          if (isSuggestedQuery(result)) {
            const query = result.result.query;
            if (recentChatTitles.has(query)) {
              return false;
            }
            return true;
          }

          return true;
        });
      }),
    );
  };
};

export const sortFlatResults: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.map((results) => results.sort(sortScoredResults(false))),
    );
  };
};

export const sortSectionedResults: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(
      op.map((results) => results.sort(sortScoredResults(true))),
    );
  };
};

export const limitResultsInSections: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = (_query, _mode, { grossLimit, sectionLimit }) => {
  return (source) => {
    return source.pipe(
      op.map((sourceResults) => {
        const priorityResults = filterAndMergeByType(
          sourceResults,
          PRIORITY_RESULT_TYPES,
        );

        const regularResults = sourceResults.filter(
          (result) => !PRIORITY_RESULT_TYPES.includes(result.type),
        );

        const dynamicSectionLimit = getDynamicSectionLimit(
          priorityResults,
          sectionLimit,
        );

        const sectionLimitedFlattenedRegularResults: typeahead.ScoredResult[] =
          orderedSectionGrouping(regularResults)
            .map((sectionedResults) =>
              sectionedResults.slice(0, dynamicSectionLimit),
            )
            .flat();

        const remainingGrossLimit = grossLimit - priorityResults.length;
        const completelyLimitedFlattenedRegularResults =
          sectionLimitedFlattenedRegularResults.slice(0, remainingGrossLimit);

        return [
          ...priorityResults,
          ...completelyLimitedFlattenedRegularResults,
        ];
      }),
    );
  };
};

export const scoreSections: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = (query, mode, { sections, cache, config }) => {
  return (source) => {
    return source.pipe(
      op.switchMap((sourceResults) => {
        return rx
          .from(
            scoring.sectionScore(
              query,
              mode,
              sourceResults,
              sections || [],
              cache,
              config,
            ),
          )
          .pipe(
            op.map((sectionScores) => {
              return sourceResults.map((sourceResult) => ({
                ...sourceResult,
                sectionScore: sectionScores[sourceResult.sectionId!],
              }));
            }),
          );
      }),
    );
  };
};

export function clampTypeahead_deprecated(
  sectionedSortedResults: typeahead.ScoredResult[],
  query: string,
  grossLimit: number,
  config: typeahead.Config,
) {
  const suggestedQuery = sectionedSortedResults.filter(
    isSuggestedQuery,
  )?.[0] as
    | ExtractTypePredicate<typeof isSuggestedQuery>
    // this can be undefined in practice, so this just forces the type to match reality
    | undefined;

  // shortcuts
  const shortcuts = sectionedSortedResults.filter(isURLShortcut);

  // recent queries
  const recentQueries = sectionedSortedResults
    .filter(isRecentQuery)
    .slice(0, query.length > 0 ? 1 : grossLimit);

  // all other sources
  const rest = sectionedSortedResults.filter(
    (result) =>
      !isSuggestedQuery(result) &&
      !isURLShortcut(result) &&
      !isRecentQuery(result),
  );

  // combine all but the suggested-search
  const combinedAndLimited = [...shortcuts, ...recentQueries, ...rest]
    .slice(0, grossLimit)
    // sort by score again to ensure they are all in the correct order post merge
    .sort(sortScoredResults(config.isOmniboxEnabled));

  // then clamp the suggested query to the first or second position depending on it's score
  // also clamp people results towards the top, usually after the suggested query
  // WARNING, THE RESULTS CANNOT BE SORTED AGAIN AFTER THIS POINT!
  return clampSomeResultsMaxPosition(suggestedQuery, combinedAndLimited, false);
}

/**
 * Organizes results by type and applies positioning rules for suggested queries
 */
export function clampSection(
  sectionedSortedResults: typeahead.ScoredResult[],
  config: typeahead.Config,
) {
  const priorityUserQuery = sectionedSortedResults.find(
    isScoredUserQueryResult,
  );

  const rest = sectionedSortedResults.filter(
    (result) => !isScoredUserQueryResult(result),
  );

  if (!priorityUserQuery) {
    return sectionedSortedResults;
  }

  return clampSomeResultsMaxPosition(
    priorityUserQuery,
    rest,
    config.isOmniboxEnabled,
  );
}

/**
 * Processes sections with proper organization and clamping
 */
export const applyUniqueRules: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = (_query, _mode, { config }) => {
  return (source) => {
    return source.pipe(
      op.map((results) =>
        orderedSectionGrouping(results)
          .map((sectionedResults) => clampSection(sectionedResults, config))
          .flat(),
      ),
      op.defaultIfEmpty([]),
    );
  };
};

/**
 * Processes sections with proper organization and clamping
 */
export const applyUniqueRules_deprecated: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = (query, _mode, { config, grossLimit }) => {
  return (source) => {
    return source.pipe(
      op.map((sortedResults) => {
        return clampTypeahead_deprecated(
          sortedResults,
          query,
          grossLimit,
          config,
        );
      }),
      op.defaultIfEmpty([]),
    );
  };
};

export const createTimeoutSafetyNet: PipelineFunctionWrapper<
  typeahead.ScoredResult[],
  typeahead.ScoredResult[]
> = () => {
  return (source) => {
    return source.pipe(op.takeUntil(rx.timer(STREAM_TIMEOUT_MS)));
  };
};
